import { useEffect, useRef, useState } from 'react';
import { numero, t } from '../i18n/idioma';
import type { WorldStatus } from '../world/types';
import { FADIGA_CACADA, FADIGA_COLETA, FADIGA_TETO, FADIGA_TREINO, FADIGA_VIGILIA, fadigaPct, tomFadiga } from '../world/fadiga';
import { Medidor } from './kit';

type BarId = 'hp' | 'mana' | 'stam' | 'fadiga';
type BarTone = 'hp' | 'mana' | 'stam' | 'poison' | 'fadiga' | 'fadiga-ambar' | 'fadiga-esgotado';

interface BarProps {
  label: string;
  value: number;
  max: number;
  tone: BarTone;
  title?: string;
  prominent?: boolean;
  marks?: readonly number[];
  markTone?: 'cura' | 'res';
  named?: boolean;
  trail?: boolean;
  /** Mostra o valor como porcentagem (a stamina): "37%" em vez de "37 / 100". */
  pct?: boolean;
}

const GHOST_MS = 500;

function Bar({ label, value, max, tone, title, prominent, marks, markTone, named, trail, pct: emPct }: BarProps) {
  const [ghost, setGhost] = useState<{ from: number; to: number } | null>(null);
  const prev = useRef(value);
  useEffect(() => {
    const before = prev.current;
    prev.current = value;
    if (!trail || value >= before) return;
    setGhost({ from: before, to: value });
    const id = window.setTimeout(() => setGhost(null), GHOST_MS);
    return () => window.clearTimeout(id);
  }, [value, trail]);
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const ghostLeft = ghost && max > 0 ? (ghost.to / max) * 100 : 0;
  const ghostWidth = ghost && max > 0 ? ((ghost.from - ghost.to) / max) * 100 : 0;
  const track = (
    <div className={prominent && !named ? 'bar__track bar__track--stamina' : 'bar__track'}>
      <div className={`bar__fill bar__fill--${tone}`} style={{ width: `${pct}%` }} />
      {ghost && ghostWidth > 0 && (
        <span
          className="bar__ghost"
          style={{ left: `${ghostLeft}%`, width: `${ghostWidth}%` }}
          aria-hidden
        />
      )}
      {marks?.map(n => (
        <span
          key={n}
          className={`bar__mark${markTone === 'res' ? ' bar__mark--res' : ''}`}
          style={{ left: `${Math.min(100, Math.max(0, n))}%` }}
          aria-hidden
        />
      ))}
      {!named && (
        <span className="bar__val">
          {prominent && <span className="bar__name">{label}</span>}
          <span>{emPct ? `${numero(Math.round(value))}%` : <>{numero(Math.round(value))} / {numero(max)}</>}</span>
        </span>
      )}
    </div>
  );
  if (named) {
    return (
      <div
        className="bar bar--named"
        role="meter"
        aria-label={label}
        aria-valuenow={Math.round(value)}
        aria-valuemin={0}
        aria-valuemax={max}
        title={title}
      >
        <span className="bar__k">{label}</span>
        {track}
        <span className="bar__n">
          <b>{numero(Math.round(value))}</b>
          {' / '}
          {numero(max)}
        </span>
      </div>
    );
  }
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={max}
      title={title}
    >
      {track}
    </div>
  );
}

const ALL: readonly BarId[] = ['fadiga', 'hp', 'mana', 'stam'];
export const COMBATE_BARS: readonly BarId[] = ['hp', 'mana', 'stam'];

export const STATUS_VAZIO: WorldStatus = {
  hp: 0, hpMax: 1, mana: 0, manaMax: 1, stam: 0, stamMax: 1,
  fadiga: FADIGA_TETO, fadigaMax: FADIGA_TETO,     fadigaFaixa: 0,
    gait: 'parado', place: '', coord: '', canal: 0, protegida: false, tileX: 0, tileY: 0, envenenado: false, criminalLeft: 0
};

function dicaFadiga(faixa: number): string {
  if (faixa === FADIGA_COLETA) return t('mundo.staminaColeta');
  if (faixa === FADIGA_CACADA) return t('mundo.staminaCacada');
  if (faixa === FADIGA_TREINO) return t('mundo.staminaTreino');
  if (faixa === FADIGA_VIGILIA) return t('mundo.staminaVigilia');
  return t('mundo.staminaRepouso');
}

/** Vida, mana, vigor e stamina de aprendizado. */
export function StatBars({
  status,
  show = ALL,
  hpMarks,
  manaMarks,
  named = false,
  anel = false
}: {
  status: WorldStatus;
  show?: readonly BarId[];
  hpMarks?: readonly number[];
  manaMarks?: readonly number[];
  named?: boolean;
  anel?: boolean;
}) {
  const fadiga = status.fadiga ?? FADIGA_TETO;
  // A stamina aparece em % (Renato, 29/10/2026): 1.440 = 100%.
  const fadigaPorCento = fadigaPct(fadiga);
  const tituloFadiga = `${t('mundo.staminaPct', { n: numero(fadigaPorCento) })} · ${dicaFadiga(status.fadigaFaixa ?? 0)}`;
  return (
    <div className="bars">
      {show.includes('fadiga') && (anel ? (
        <Medidor
          forma="anel"
          valor={fadigaPorCento}
          max={100}
          unidade="%"
          tom={tomFadiga(fadiga)}
          icone="stamina"
          rotulo={t('mundo.stamina')}
          title={tituloFadiga}
        />
      ) : (
        <Bar
          label={t('mundo.stamina')}
          value={fadigaPorCento}
          max={100}
          pct
          tone={tomFadiga(fadiga)}
          title={tituloFadiga}
          prominent
        />
      ))}
      {show.includes('hp') && (
        <Bar
          label={t('mundo.vida')}
          value={status.hp}
          max={status.hpMax}
          tone={status.envenenado ? 'poison' : 'hp'}
          marks={hpMarks}
          named={named}
          trail={named}
        />
      )}
      {show.includes('mana') && (
        <Bar
          label={t('mundo.mana')}
          value={status.mana}
          max={status.manaMax}
          tone="mana"
          marks={manaMarks}
          markTone="res"
          named={named}
        />
      )}
      {show.includes('stam') && (
        <Bar
          label={t('mundo.vigor')}
          value={status.stam}
          max={status.stamMax}
          tone="stam"
          named={named}
        />
      )}
    </div>
  );
}
